// 2P AI — hook dati condivisi (react-query). Le chiavi sono condivise tra le schermate, così la
// cache deduplica le richieste (es. /sessions usata da Progetti, Dettaglio e Server). Forme dei
// dati verificate sui handler PHP (vedi docs/redesign-2pai.md, contratti R1).
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { isWorking } from './sessions';
import type { Project, SessionSummary, Server, StatusSnapshot } from './types';

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ items: Project[] }>('/projects').then((d) => d.items),
    retry: false,
    refetchInterval: 30_000,
  });
}

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<{ items: SessionSummary[] }>('/sessions').then((d) => d.items),
    retry: false,
    refetchInterval: 5_000,
  });
}

export function useServers() {
  return useQuery({
    queryKey: ['servers'],
    queryFn: () => api.get<{ items: Server[] }>('/servers').then((d) => d.items),
    retry: false,
    refetchInterval: 30_000,
  });
}

export function useStatus() {
  return useQuery({
    queryKey: ['status'],
    queryFn: () => api.get<StatusSnapshot>('/status'),
    retry: false,
    refetchInterval: 15_000,
  });
}

// Chiude una sessione (POST /sessions/{id}/cancel → 202). Niente step-up (come kill-switch).
// La risposta {session} non è usata → tipo `unknown`. Errori non silenziosi: log + gestiti da useCloseSession.
export function useCancelSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/sessions/${id}/cancel`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      void qc.invalidateQueries({ queryKey: ['status'] });
    },
    onError: (e) => { console.error('Chiusura sessione fallita:', e); },
  });
}

// Logica di chiusura condivisa tra Progetti e Dettaglio (§10): conferma per le sessioni che stanno
// lavorando, conferma sempre per la chiusura in blocco, e feedback se qualcosa fallisce.
// Elimina una sessione conclusa (DELETE /sessions/{id}) e il suo storico.
export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/sessions/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      void qc.invalidateQueries({ queryKey: ['status'] });
    },
    onError: (e) => { console.error('Eliminazione sessione fallita:', e); },
  });
}

export function useCloseSession() {
  const cancel = useCancelSession();
  const del = useDeleteSession();

  async function closeOne(s: SessionSummary): Promise<void> {
    if (isWorking(s.status) && !window.confirm('Questa sessione sta lavorando. Vuoi interromperla?')) return;
    try {
      await cancel.mutateAsync(s.id);
    } catch {
      window.alert('Impossibile chiudere la sessione. Riprova.');
    }
  }

  async function closeMany(list: SessionSummary[], message: string): Promise<void> {
    if (list.length === 0) return;
    if (!window.confirm(message)) return;
    const results = await Promise.allSettled(list.map((s) => cancel.mutateAsync(s.id)));
    if (results.some((r) => r.status === 'rejected')) window.alert('Alcune sessioni non sono state chiuse. Riprova.');
  }

  async function deleteOne(s: SessionSummary): Promise<void> {
    if (!window.confirm('Eliminare questa sessione e il suo storico? Non si può annullare.')) return;
    try {
      await del.mutateAsync(s.id);
    } catch {
      window.alert('Impossibile eliminare la sessione. Riprova.');
    }
  }

  async function deleteMany(list: SessionSummary[], message: string): Promise<void> {
    if (list.length === 0) return;
    if (!window.confirm(message)) return;
    const results = await Promise.allSettled(list.map((s) => del.mutateAsync(s.id)));
    if (results.some((r) => r.status === 'rejected')) window.alert('Alcune sessioni non sono state eliminate. Riprova.');
  }

  return { closeOne, closeMany, deleteOne, deleteMany };
}
